import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/quickbooks`;

// Configuration: Set to true to use improved WebClient endpoints
const USE_WEBCLIENT_ENDPOINTS = true;

// Flag to prevent duplicate token exchange requests (race condition protection)
let isProcessingTokenExchange = false;

// Get token from localStorage
const token = localStorage.getItem('token');

export const getQuickbookCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  console.log('🔍 Frontend: getQuickbookCredentialsAndCreateToken() called:');
  console.log('   - Connector ID:', connectorId);
  console.log('   - Master Connector ID:', mcId);
  console.log('   - Master Connectors count:', masterConnectors?.length);
  console.log('   - Current time:', new Date().toISOString());

  try {
    const norm = (s) =>
      (s || '')
        .toString()
        .trim()
        .toUpperCase()
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ');
    
    // Find the Quickbook master connector details
    // Prefer connector type as the stable identifier; fallback to name.
    const quickbookConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === mcId?.toString() ||
       mc.mcId?.toString() === mcId?.toString()) &&
      (() => {
        const typeNorm = norm(mc.mcConnectorType);
        const nameNorm = norm(mc.mcName);
        const source = typeNorm || nameNorm;
        return source.includes('QUICKBOOK');
      })()
    );
    
    console.log('   - Quickbook connector found:', !!quickbookConnector);
    if (quickbookConnector) {
      console.log('   - Connector details:', {
        mcId: quickbookConnector.mcId,
        mcName: quickbookConnector.mcName,
        mcRecId: quickbookConnector.mcRecId
      });
    }

    if (!quickbookConnector) {
      console.error('   ❌ Quickbook master connector not found');
      throw new Error('Quickbook master connector not found');
    }

    console.log('   - Fetching connector parameters from API...');
    console.log('   - Using connector instance ID (ctd_id):', connectorId);
    // Fetch client ID and client secret from the API endpoint using connector instance ID
    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });

    console.log('   - Parameter response status:', paramResponse.status);
    console.log('   - Parameter response ok:', paramResponse.ok);

    if (!paramResponse.ok) {
      console.error('   ❌ Failed to fetch connector parameters:', paramResponse.status);
      throw new Error(`Failed to fetch connector parameters: ${paramResponse.status}`);
    }
    
    const connectorParams = await paramResponse.json();
    console.log('   - Connector parameters received:', connectorParams?.length, 'parameters');
    console.log('   - Parameter names:', connectorParams?.map(p => p.scpName));

    const clientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;

    console.log('   - Client ID found:', !!clientId);

    if (!clientId) {
      console.error('   ❌ Client ID not found in connector parameters');
      throw new Error('Client ID not found in connector parameters');
    }

    // Store the connector ID for after redirect
    localStorage.setItem('PendingQuickbookConnectorId', connectorId);
    console.log('   - Stored PendingQuickbookConnectorId:', connectorId);
    // Clear previous realm cache before starting a new auth cycle for this connector.
    localStorage.removeItem(`QuickbookRealmId_${connectorId}`);

    // Check if we already have a stored authorization code
    const storedAuthCode = localStorage.getItem('AuthCode');
    console.log('   - Stored authorization code found:', !!storedAuthCode);
    if (storedAuthCode) {
      console.log('   - Stored auth code preview:', storedAuthCode.substring(0, 20) + '...');
    }

    if (storedAuthCode) {
      console.log('   - Exchanging stored authorization code for token...');
      // If we have a stored authorization code, exchange it for an access token
      const realmIdFromCallback = localStorage.getItem(`QuickbookRealmId_${connectorId}`);
      console.log('   - QuickbookRealmId from callback(localStorage):', realmIdFromCallback ? realmIdFromCallback : 'NULL');
      const tokenResponse = await exchangeAuthCodeForToken(
        storedAuthCode,
        clientId,
        mcId,
        realmIdFromCallback
      );

      console.log('   - Token exchange completed');
      console.log('   - Token response keys:', tokenResponse ? Object.keys(tokenResponse) : 'NULL');

      // Clean up the stored authorization code
      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingQuickbookConnectorId');
      localStorage.removeItem(`QuickbookRealmId_${connectorId}`);
      console.log('   - Cleaned up stored authorization code');

      // Return the access token
      return tokenResponse;
    } else {
      console.log('   - No stored authorization code, generating OAuth URL...');
      // Use Master Connector auth URL
      const authEndpoint = quickbookConnector.mcAuthurl;
      if (!authEndpoint) {
        throw new Error('QuickBooks Authentication URL is missing from Master Connector data');
      }
      const quickbookAuthUrl = `${authEndpoint}${authEndpoint.includes('?') ? '&' : '?'}client_id=${clientId}&scope=com.intuit.quickbooks.accounting&redirect_uri=${process.env.REACT_APP_OAUTH_REDIRECT_URI}&response_type=code&state=${connectorId}`;
      
      console.log('   - OAuth URL generated:');
      console.log('     - Client ID:', clientId.substring(0, 20) + '...');
      console.log('     - Redirect URI:', process.env.REACT_APP_OAUTH_REDIRECT_URI);
      console.log('     - State (Connector ID):', connectorId);
      console.log('     - Full URL:', quickbookAuthUrl);

      // Redirect to Quickbook authentication
      console.log('   - Redirecting to QuickBooks authentication...');
      window.location.href = quickbookAuthUrl;
      
      // Return null since we're redirecting
      return null;
    }
  } catch (error) {
    console.error('Error getting Quickbook credentials:', error);
    throw error;
  }
};

// Helper function to exchange authorization code for token
export const exchangeAuthCodeForToken = async (authCode, clientId, mcId, realmIdFromCallback) => {
  console.log('🔍 Frontend: exchangeAuthCodeForToken() called:');
  console.log('   - Authorization Code:', authCode ? authCode.substring(0, 20) + '...' : 'NULL');
  console.log('   - Client ID:', clientId ? clientId.substring(0, 20) + '...' : 'NULL');
  console.log('   - Master Connector ID:', mcId);
  console.log('   - realmId (real.mid) from callback:', realmIdFromCallback ? realmIdFromCallback : 'NULL');
  console.log('   - Current time:', new Date().toISOString());

  // Prevent duplicate token exchange requests (protection against React Strict Mode double renders)
  if (isProcessingTokenExchange) {
    console.warn('⚠️ Token exchange already in progress, skipping duplicate request to prevent auth code reuse');
    console.warn('   - This prevents the "Authorization code incorrect" error from duplicate calls');
    return null; // Return null to indicate this is a duplicate call
  }

  // Set flag to prevent concurrent calls
  isProcessingTokenExchange = true;
  console.log('   ✅ Token exchange lock acquired, proceeding with request');

  try {
    // Choose endpoint based on configuration
    const endpoint = USE_WEBCLIENT_ENDPOINTS ? '/authTokenWebClient' : '/authToken';
    // Pass mcId parameter (backend will intelligently handle it as ctd_id or mc_id)
    const realmId = realmIdFromCallback;
    const tokenUrl = `${API_BASE_URL}${endpoint}?mcId=${mcId}${realmId ? `&realmid=${encodeURIComponent(realmId)}` : ''}`;
    console.log('   - Token URL:', tokenUrl);
    console.log('   - Using ID parameter (could be ctd_id or mc_id):', mcId);
    console.log('   - Using WebClient endpoints:', USE_WEBCLIENT_ENDPOINTS);

    console.log('   - Making POST request to backend...');
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`, // Retain Bearer token for backend auth
      },
      mode: 'cors',
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: authCode,
        redirect_uri: process.env.REACT_APP_OAUTH_REDIRECT_URI
      })
    });

    console.log('   - Request body prepared:');
    console.log('     - grant_type: authorization_code');
    console.log('     - code:', authCode.substring(0, 20) + '...');
    console.log('     - redirect_uri:', process.env.REACT_APP_OAUTH_REDIRECT_URI);
    console.log('   - Response status:', response.status);
    console.log('   - Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('   ❌ Token exchange error response:', errorText);
      console.error('   - Response status:', response.status);
      console.error('   - Response status text:', response.statusText);
      throw new Error(`Failed to exchange authorization code for token: ${response.status}`);
    }

    const tokenData = await response.json();

    // 🔑 LOG FRESH TOKEN RECEIVED FROM QUICKBOOKS
    console.log('🔑 FRESH QUICKBOOKS TOKEN RECEIVED FROM API:');
    console.log('=====================================');
    console.log('📥 RAW TOKEN DATA FROM QUICKBOOKS:');
    console.log('   - Response keys:', Object.keys(tokenData));
    console.log('   - Raw JSON response:', JSON.stringify(tokenData, null, 2));
    console.log('🔐 TOKEN DETAILS:');
    if (tokenData.access_token) {
      console.log('   ✅ Access Token (first 50 chars):', tokenData.access_token.substring(0, 50) + '...');
      console.log('   📏 Access Token Length:', tokenData.access_token.length);
    }
    if (tokenData.refresh_token) {
      console.log('   ✅ Refresh Token (first 30 chars):', tokenData.refresh_token.substring(0, 30) + '...');
      console.log('   📏 Refresh Token Length:', tokenData.refresh_token.length);
    }
    if (tokenData.token_type) {
      console.log('   ⏰ Token Type:', tokenData.token_type);
    }
    if (tokenData.expires_in) {
      console.log('   ⏱️ Expires In:', tokenData.expires_in, 'seconds (' + Math.round(tokenData.expires_in/60) + ' minutes)');
    }
    if (tokenData.scope) {
      console.log('   🔒 Scope:', tokenData.scope);
    }
    if (tokenData.realmId) {
      console.log('   🏢 Realm ID:', tokenData.realmId);
    }
    console.log('   🕐 Token Received At:', new Date().toISOString());
    console.log('=====================================');

    // COMMENTED OUT: Auto-display of token modal - disabled for production (high risk)
    // 🖥️ DISPLAY TOKEN IN UI FOR 1 MINUTE
    // displayTokenInUI(tokenData);

    // Get the connector ID that was stored before redirect
    const connectorId = localStorage.getItem('PendingQuickbookConnectorId');
    console.log('   - Pending connector ID:', connectorId);

    if (connectorId) {
      console.log('   - Updating connector with token data...');
      // Update the connector with token data
      const CONNECTOR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;
      console.log('   - Connector API URL:', CONNECTOR_API_URL);

      try {
        // First, get the current connector data
        const connectorResponse = await fetch(`${CONNECTOR_API_URL}/${connectorId}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
        if (!connectorResponse.ok) {
          throw new Error(`Failed to fetch connector data: ${connectorResponse.status}`);
        }
        const connectorData = await connectorResponse.json();
        
        // Calculate token expiry dates
        const expiresIn = parseInt(tokenData.expires_in) || 3600;
        const tokenExpiry = new Date(Date.now() + expiresIn * 1000).toISOString();
        
        // QuickBooks refresh tokens are valid for 100 days
        const HUNDRED_DAYS_MS = 100 * 24 * 60 * 60 * 1000;
        const refreshTokenExpiry = new Date(Date.now() + HUNDRED_DAYS_MS).toISOString();
        
        // Prepare updated connector data
        const updatedConnectorData = {
          ...connectorData,
          token: tokenData.access_token,
          refreshToken: tokenData.refresh_token,
          tokenExpiry: tokenExpiry,
          refreshUrl: connectorData.refreshUrl,
          refreshTokenExpiry: refreshTokenExpiry,
          status: 'Connected',
          connectedTime: new Date().toISOString()
        };

        // 🔑 LOG QUICKBOOKS TOKEN BEING STORED IN DATABASE
        console.log('🔑 QUICKBOOKS TOKEN STORAGE IN DATABASE:');
        console.log('=====================================');
        console.log('🏦 DATABASE STORAGE - QuickBooks Token Details:');
        console.log('   📝 Connector ID:', connectorId);
        console.log('   🔐 Access Token:', tokenData.access_token ? tokenData.access_token.substring(0, 50) + '...' : 'NULL');
        console.log('   🔄 Refresh Token:', tokenData.refresh_token ? tokenData.refresh_token.substring(0, 30) + '...' : 'NULL');
        console.log('   ⏰ Token Type:', tokenData.token_type || 'Bearer');
        console.log('   ⏱️ Expires In:', expiresIn, 'seconds (' + Math.round(expiresIn/60) + ' minutes)');
        console.log('   📅 Refresh Token Expiry:', refreshTokenExpiry);
        console.log('   🔗 Refresh URL:', connectorData.mcRefreshUrl || connectorData.refreshUrl);
        console.log('   📊 Status:', 'Connected');
        console.log('   🕐 Connected Time:', new Date().toISOString());
        console.log('   💾 About to store in database via PUT API...');
        console.log('=====================================');

        // Make the PUT request to update the connector
        const updateResponse = await fetch(`${CONNECTOR_API_URL}/${connectorId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedConnectorData)
        });
        
        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error('❌ Update connector error:', errorText);
          console.error('❌ PUT API Response Status:', updateResponse.status);
          console.error('❌ PUT API Response Status Text:', updateResponse.statusText);
          throw new Error(`Failed to update connector with tokens: ${updateResponse.status}`);
        }

        // 🎉 LOG SUCCESSFUL DATABASE STORAGE
        console.log('✅ QUICKBOOKS TOKEN SUCCESSFULLY STORED IN DATABASE!');
        console.log('=====================================');
        console.log('🎉 DATABASE STORAGE SUCCESS:');
        console.log('   ✅ PUT API Response Status:', updateResponse.status);
        console.log('   ✅ PUT API Response OK:', updateResponse.ok);
        console.log('   💾 Token stored successfully in database');
        console.log('   📝 Connector ID:', connectorId);
        console.log('   📊 New Status: Connected');
        console.log('   🕐 Storage completed at:', new Date().toISOString());

        // Get the updated data from response if available
        try {
          const updatedResponseData = await updateResponse.json();
          console.log('   📦 Response data keys:', Object.keys(updatedResponseData));
          if (updatedResponseData.token) {
            console.log('   🔐 Confirmed stored access token preview:', updatedResponseData.token.substring(0, 30) + '...');
          }
          if (updatedResponseData.refreshToken) {
            console.log('   🔄 Confirmed stored refresh token preview:', updatedResponseData.refreshToken.substring(0, 20) + '...');
          }
        } catch (e) {
          console.log('   📝 Response parsing skipped (no JSON response)');
        }
        console.log('=====================================');

        // Do not reload the page here.
        // Reload can cause the OAuth callback flow to re-run and exchange the same auth code twice.
        // ConnectorToken.jsx already updates UI and clears localStorage keys after the exchange.
        localStorage.setItem('freshQuickbooksToken', JSON.stringify({
          ...tokenData,
          storedAt: new Date().toISOString(),
          connectorId: connectorId
        }));
      } catch (error) {
        console.error('Error updating connector with tokens:', error);
      }
    }
    
    return tokenData;
  } catch (error) {
    console.error('Token exchange error:', error);
    throw error;
  } finally {
    // Always reset the flag, even if there's an error
    isProcessingTokenExchange = false;
    console.log('   🔓 Token exchange lock released');
  }
};

// Function to display COMPLETE token in UI permanently
const displayTokenInUI = (tokenData) => {
  // Remove any existing token display first
  const existingDisplay = document.getElementById('quickbooks-token-display');
  if (existingDisplay) {
    existingDisplay.remove();
  }

  // Add a delay to ensure the display shows before any page reload
  setTimeout(() => {
    createTokenDisplay(tokenData);
  }, 100);
};

// Function to check for stored tokens on page load and display them
const checkForStoredTokens = () => {
  const storedToken = localStorage.getItem('freshQuickbooksToken');
  if (storedToken) {
    try {
      const tokenData = JSON.parse(storedToken);
      // Only show if stored within last 10 minutes (in case of multiple connections)
      const storedTime = new Date(tokenData.storedAt);
      const now = new Date();
      const timeDiff = (now - storedTime) / (1000 * 60); // in minutes

      if (timeDiff <= 10) {
        console.log('🔍 Found stored QuickBooks token, displaying...');
        createTokenDisplay(tokenData);
      } else {
        // Clean up old stored token
        localStorage.removeItem('freshQuickbooksToken');
      }
    } catch (e) {
      console.error('Error parsing stored token:', e);
      localStorage.removeItem('freshQuickbooksToken');
    }
  }
};

// Actual token display creation function
const createTokenDisplay = (tokenData) => {
  // Remove any existing display first
  const existingDisplay = document.getElementById('quickbooks-token-display');
  if (existingDisplay) {
    existingDisplay.remove();
  }

  // Create a floating div to display COMPLETE token information
  const tokenDisplay = document.createElement('div');
  tokenDisplay.id = 'quickbooks-token-display';
  tokenDisplay.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    width: 600px;
    max-height: 90vh;
    background: linear-gradient(135deg, #1976d2, #42a5f5);
    color: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
    font-family: 'Roboto', 'Arial', sans-serif;
    font-size: 12px;
    z-index: 10000;
    overflow-y: auto;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.2);
    animation: slideInRight 0.5s ease-out;
    resize: both;
  `;

  // Add animation keyframes to document
  if (!document.getElementById('token-display-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'token-display-styles';
    styleSheet.textContent = `
      @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOutRight {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
      #quickbooks-token-display:hover {
        transform: scale(1.01);
        transition: transform 0.2s ease;
      }
      .token-text {
        user-select: all;
        -webkit-user-select: all;
        -moz-user-select: all;
        -ms-user-select: all;
      }
    `;
    document.head.appendChild(styleSheet);
  }

  tokenDisplay.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; border-bottom: 1px solid rgba(255,255,255,0.3); padding-bottom: 10px;">
      <h3 style="margin: 0; color: #fff; font-size: 16px;">🔑 QuickBooks Complete Token</h3>
      <div style="display: flex; align-items: center; gap: 10px;">
        <span style="background: rgba(255,255,255,0.2); padding: 4px 8px; border-radius: 20px; font-weight: bold; font-size: 11px; color: #4caf50;">PERMANENT</span>
        <button id="copy-all-tokens" style="background: rgba(255,255,255,0.2); border: none; color: white; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 11px; font-weight: bold;">📋 Copy All</button>
        <button id="close-token-display" style="background: rgba(255,255,255,0.2); border: none; color: white; width: 24px; height: 24px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: bold;">×</button>
      </div>
    </div>

    <div style="max-height: 80vh; overflow-y: auto;">
      <div style="margin-bottom: 15px;">
        <div style="color: #e3f2fd; font-size: 12px; margin-bottom: 6px; font-weight: bold; display: flex; justify-content: space-between; align-items: center;">
          🔐 ACCESS TOKEN (COMPLETE)
          <button class="copy-btn" data-copy="${tokenData.access_token || ''}" style="background: rgba(0,0,0,0.3); border: none; color: #4fc3f7; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 9px;">Copy</button>
        </div>
        <div class="token-text" style="background: rgba(0,0,0,0.4); padding: 12px; border-radius: 8px; word-break: break-all; font-family: 'Courier New', monospace; font-size: 9px; color: #4fc3f7; border: 1px solid rgba(79, 195, 247, 0.3); max-height: 150px; overflow-y: auto;">
          ${tokenData.access_token || 'Not available'}
        </div>
        <div style="color: #90caf9; font-size: 10px; margin-top: 4px;">Length: ${tokenData.access_token ? tokenData.access_token.length + ' characters' : 'N/A'}</div>
      </div>

      <div style="margin-bottom: 15px;">
        <div style="color: #e3f2fd; font-size: 12px; margin-bottom: 6px; font-weight: bold; display: flex; justify-content: space-between; align-items: center;">
          🔄 REFRESH TOKEN (COMPLETE)
          <button class="copy-btn" data-copy="${tokenData.refresh_token || ''}" style="background: rgba(0,0,0,0.3); border: none; color: #81c784; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 9px;">Copy</button>
        </div>
        <div class="token-text" style="background: rgba(0,0,0,0.4); padding: 12px; border-radius: 8px; word-break: break-all; font-family: 'Courier New', monospace; font-size: 9px; color: #81c784; border: 1px solid rgba(129, 199, 132, 0.3); max-height: 120px; overflow-y: auto;">
          ${tokenData.refresh_token || 'Not available'}
        </div>
        <div style="color: #90caf9; font-size: 10px; margin-top: 4px;">Length: ${tokenData.refresh_token ? tokenData.refresh_token.length + ' characters' : 'N/A'}</div>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px;">
        <div>
          <div style="color: #e3f2fd; font-size: 11px; margin-bottom: 4px; font-weight: bold;">⏰ TOKEN TYPE</div>
          <div class="token-text" style="background: rgba(0,0,0,0.2); padding: 8px; border-radius: 6px; font-size: 12px; color: #ffb74d; border: 1px solid rgba(255, 183, 77, 0.3);">
            ${tokenData.token_type || 'Bearer'}
          </div>
        </div>
        <div>
          <div style="color: #e3f2fd; font-size: 11px; margin-bottom: 4px; font-weight: bold;">⏱️ EXPIRES IN</div>
          <div class="token-text" style="background: rgba(0,0,0,0.2); padding: 8px; border-radius: 6px; font-size: 12px; color: #f48fb1; border: 1px solid rgba(244, 143, 177, 0.3);">
            ${tokenData.expires_in ? tokenData.expires_in + ' seconds (' + Math.round(tokenData.expires_in/60) + ' minutes)' : 'N/A'}
          </div>
        </div>
      </div>

      ${tokenData.scope ? `
        <div style="margin-bottom: 12px;">
          <div style="color: #e3f2fd; font-size: 11px; margin-bottom: 4px; font-weight: bold;">🔒 SCOPE</div>
          <div class="token-text" style="background: rgba(0,0,0,0.2); padding: 8px; border-radius: 6px; font-size: 11px; color: #ce93d8; border: 1px solid rgba(206, 147, 216, 0.3);">
            ${tokenData.scope}
          </div>
        </div>
      ` : ''}

      ${tokenData.realmId ? `
        <div style="margin-bottom: 12px;">
          <div style="color: #e3f2fd; font-size: 11px; margin-bottom: 4px; font-weight: bold;">🏢 REALM ID</div>
          <div class="token-text" style="background: rgba(0,0,0,0.2); padding: 8px; border-radius: 6px; font-size: 12px; color: #80cbc4; border: 1px solid rgba(128, 203, 196, 0.3);">
            ${tokenData.realmId}
          </div>
        </div>
      ` : ''}

      <div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.2); font-size: 10px; color: #b3e5fc;">
        🕐 Retrieved: ${new Date().toLocaleString()}<br/>
        💾 Status: Being stored in database...<br/>
        ♾️ Display: Permanent (until manually closed)
      </div>
    </div>
  `;

  // Add to body
  document.body.appendChild(tokenDisplay);

  // Add close button functionality
  document.getElementById('close-token-display').addEventListener('click', () => {
    // Clear stored token when manually closed
    localStorage.removeItem('freshQuickbooksToken');
    tokenDisplay.style.animation = 'slideOutRight 0.3s ease-in';
    setTimeout(() => tokenDisplay.remove(), 300);
  });

  // Add copy functionality for individual tokens
  tokenDisplay.addEventListener('click', (e) => {
    if (e.target.classList.contains('copy-btn')) {
      const textToCopy = e.target.getAttribute('data-copy');
      navigator.clipboard.writeText(textToCopy).then(() => {
        e.target.textContent = 'Copied!';
        e.target.style.background = 'rgba(76, 175, 80, 0.3)';
        setTimeout(() => {
          e.target.textContent = 'Copy';
          e.target.style.background = 'rgba(0,0,0,0.3)';
        }, 2000);
      });
    }
  });

  // Add copy all tokens functionality
  document.getElementById('copy-all-tokens').addEventListener('click', () => {
    const allTokenData = JSON.stringify(tokenData, null, 2);
    navigator.clipboard.writeText(allTokenData).then(() => {
      const btn = document.getElementById('copy-all-tokens');
      btn.textContent = '✅ Copied All!';
      btn.style.background = 'rgba(76, 175, 80, 0.3)';
      setTimeout(() => {
        btn.textContent = '📋 Copy All';
        btn.style.background = 'rgba(255,255,255,0.2)';
      }, 3000);
    });
  });

  console.log('🖥️ COMPLETE token displayed permanently in UI');
};

// COMMENTED OUT: Auto-display of token modal on page load - disabled for production (high risk)
// Check for stored tokens when the page loads
// if (typeof window !== 'undefined') {
//   // Wait for DOM to be ready
//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', checkForStoredTokens);
//   } else {
//     // DOM is already ready
//     setTimeout(checkForStoredTokens, 500);
//   }
// }